import React, { useState, useEffect, useRef } from 'react'
import { Button, Collapse, Alert, IconButton, Tooltip } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import SyncIcon from '@mui/icons-material/Sync'
import { apiUrl, wsUrl } from '../../environments/environments'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import AppDialog from '../dialog/AppDialog'
import AppButton from './AppButton'

const spin = {
    '0%': {
        transform: 'rotate(0deg)',
    },
    '100%': {
        transform: 'rotate(360deg)',
    },
}

const spinAnimation = keyframes(spin)
const NO_RESUME_PRODUCTS = []

const SyncPriceButton = ({
    builder,
    product,
    onSyncComplete,
    onError,
    initialMessage = 'Sync Prices',
    buttonStyle = {},
    buttonProps = {},
    className = '',
    showInlineError = true,
    syncUrl,
    syncMethod = 'get',
    syncParams = { builder, product },
    syncBody = null,
    confirmation,
    onSyncStateChange,
    onConfirmationStateChange,
    iconOnly = false,
    onProgressChange,
    externalProgress = null,
    resumeProducts = NO_RESUME_PRODUCTS,
    progressSuffix = '',
}) => {
    const [syncJobId, setSyncJobId] = useState(null)
    const [syncing, setSyncing] = useState(false)
    const [progressMessage, setProgressMessage] = useState('Syncing...')
    const [errorMessage, setErrorMessage] = useState('')
    const [confirmationRequest, setConfirmationRequest] = useState(null)
    const wsRef = useRef(null)
    const submissionLock = useRef(false)
    const storageKey = useRef(null)
    const runningSuffix = useRef('')

    useEffect(() => {
        onSyncStateChange?.(syncing)
    }, [syncing, onSyncStateChange])

    useEffect(() => {
        onConfirmationStateChange?.(Boolean(confirmationRequest))
    }, [confirmationRequest, onConfirmationStateChange])

    useEffect(() => {
        onProgressChange?.(syncing ? progressMessage : '')
    }, [syncing, progressMessage, onProgressChange])

    const clearError = () => {
        setErrorMessage('')
        if (onError) {
            onError('')
        }
    }

    const reportError = (message, rawError = null) => {
        setErrorMessage(message)
        if (onError) {
            onError(message, rawError)
        }
    }

    // Check for existing job on mount
    useEffect(() => {
        const key = [product || 'all', ...resumeProducts]
            .map((item) => `${builder}-${item}-syncId`)
            .find((item) => localStorage.getItem(item))
        const storedJobId = key && localStorage.getItem(key)
        if (storedJobId) {
            storageKey.current = key
            runningSuffix.current = localStorage.getItem(`${key}-progressSuffix`) || ''
            setSyncJobId(storedJobId)
            setSyncing(true)
        }
    }, [builder, product, resumeProducts])

    // Handle WebSocket connection when jobId changes
    useEffect(() => {
        if (syncJobId && syncing) {
            connectWebSocket(syncJobId)
        }

        return () => {
            if (wsRef.current) {
                wsRef.current.close()
                wsRef.current = null
            }
        }
    }, [syncJobId, syncing])

    const connectWebSocket = (jobId) => {
        try {
            const token = localStorage.getItem('msal.idtoken') || ''
            const wsConnection = new WebSocket(`${wsUrl}?token=${token}`)
            wsRef.current = wsConnection

            wsConnection.onopen = () => {
                wsConnection.send(JSON.stringify({ jobId }))
            }

            wsConnection.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    setProgressMessage(data.status || 'Processing...')

                    if (data.error) {
                        reportError(data.error, data)
                    }

                    if (data.completed) {
                        setSyncing(false)
                        localStorage.removeItem(storageKey.current)
                        localStorage.removeItem(`${storageKey.current}-progressSuffix`)
                        setSyncJobId(null)

                        if (!data.error) {
                            clearError()
                            if (onSyncComplete) {
                                onSyncComplete()
                            }
                        }

                        if (wsRef.current) {
                            wsRef.current.close()
                            wsRef.current = null
                        }
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error)
                }
            }

            wsConnection.onerror = (error) => {
                console.error('WebSocket error:', error)
                reportError('WebSocket connection error. Please try again later.', error)
                setSyncing(false)
            }

            wsConnection.onclose = () => {}
        } catch (error) {
            console.error('Error creating WebSocket:', error)
            reportError('Failed to establish WebSocket connection.', error)
            setSyncing(false)
        }
    }

    const syncPrices = async (requestParams = syncParams, requestBody = syncBody, suffix = progressSuffix) => {
        if (submissionLock.current || syncing) return
        submissionLock.current = true
        runningSuffix.current = suffix
        try {
            setSyncing(true)
            setProgressMessage('Starting...')
            clearError()

            const { data: responseData } = await ApiService(
                syncUrl || `${apiUrl}/SAPPrices/SynchronizePricesWithSap`,
                syncMethod,
                requestBody,
                null,
                0,
                requestParams
            )

            const { jobId } = responseData

            if (!jobId) {
                reportError('No jobId received from server', responseData)
                setSyncing(false)
                return
            }

            storageKey.current = `${builder}-${product || 'all'}-syncId`
            localStorage.setItem(storageKey.current, jobId)
            if (suffix) localStorage.setItem(`${storageKey.current}-progressSuffix`, suffix)
            else localStorage.removeItem(`${storageKey.current}-progressSuffix`)
            setSyncJobId(jobId)
        } catch (error) {
            console.error('Error syncing prices:', error)
            const detail = error.response?.data?.error
            reportError((typeof detail === 'string' ? detail : detail?.message) || error.message || 'Failed to start price synchronization.', error)
            setSyncing(false)
        } finally {
            submissionLock.current = false
        }
    }

    const requestSync = () => {
        if (syncing || externalProgress !== null || submissionLock.current || buttonProps.disabled) return
        if (confirmation) {
            setConfirmationRequest({ ...confirmation, params: syncParams, body: syncBody, progressSuffix })
        } else {
            syncPrices()
        }
    }

    const busy = syncing || externalProgress !== null
    const label = externalProgress ?? (syncing ? `${progressMessage || 'Syncing...'}${runningSuffix.current}` : initialMessage)
    const syncIcon = <SyncIcon sx={busy ? { animation: `${spinAnimation} 2s linear infinite` } : {}} />

    return (
        <>
            {iconOnly ? (
                <Tooltip title={label}>
                    <span>
                        <IconButton
                            color='primary'
                            aria-label={initialMessage}
                            size='small'
                            onClick={requestSync}
                            {...buttonProps}
                            disabled={busy || Boolean(buttonProps.disabled)}>
                            {syncIcon}
                        </IconButton>
                    </span>
                </Tooltip>
            ) : (
                <AppButton
                    id={'sync_prices'}
                    color='primary'
                    variant='contained'
                    size='medium'
                    className={className}
                    style={buttonStyle}
                    startIcon={syncIcon}
                    onClick={requestSync}
                    {...buttonProps}
                    disabled={busy || Boolean(buttonProps.disabled)}>
                    {label}
                </AppButton>
            )}
            <AppDialog
                open={Boolean(confirmationRequest)}
                onClose={() => setConfirmationRequest(null)}
                title={confirmationRequest?.title || 'Confirm price refresh'}
                maxWidth='sm'
                actions={
                    <>
                        <Button onClick={() => setConfirmationRequest(null)}>Cancel</Button>
                        <Button
                            variant='contained'
                            disabled={syncing || confirmationRequest?.confirmDisabled}
                            onClick={() => {
                                const params = confirmationRequest.params
                                const body = confirmationRequest.body
                                const suffix = confirmationRequest.progressSuffix
                                setConfirmationRequest(null)
                                syncPrices(params, body, suffix)
                            }}>
                            Confirm
                        </Button>
                    </>
                }>
                {confirmationRequest?.message}
            </AppDialog>
            {showInlineError && (
                <Collapse in={!!errorMessage}>
                    <Alert severity='error' onClose={clearError} sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                </Collapse>
            )}
        </>
    )
}

export default SyncPriceButton
